import * as yup from 'yup';
import { v4 } from 'uuid';

import type { DistributiveOmit } from '../../../client/internal';
import { IndicatorColor, IndicatorType, NotificationIndicator } from '../../../client/indicator';
import type { ButtonOptions, BaseNotificationOptions, NotificationStream } from '../../../client';
import { formSchema } from '../forms/fieldSchemas';
import {
  NotificationOptions,
  TemplateList,
  TemplateCustom,
  TemplateMarkdown,
  TemplateNames,
  SectionAlignment,
  TemplateFragment,
  CustomTemplateData,
  TemplateFragmentNames,
} from '../../../client/templates/templates';
import { FlatLayout } from '../../utils/templates/flattenLayout';
import { GetTemplateName } from './isTemplate';
import { getTemplateAPIVersion } from '../../utils/version';

export const StreamSchema: yup.SchemaOf<NotificationStream> = yup
  .object({
    displayName: yup.string().required(),
    id: yup.string().required(),
    appId: yup.string().required(),
  })
  .label('Stream');

export const IndicatorSchema: yup.SchemaOf<NotificationIndicator> = yup
  .object({
    type: yup
      .mixed<IndicatorType>()
      .oneOf(Object.values(IndicatorType) as IndicatorType[])
      .optional(),
    text: yup
      .mixed()
      .optional()
      // When `text` is undefined use `type`'s value as the `text`
      .when('type', (type: IndicatorType, schema: yup.StringSchema) => {
        return schema.default(type);
      }),
    color: yup
      .mixed<IndicatorColor>()
      .oneOf(Object.values(IndicatorColor) as IndicatorColor[])
      .optional()
      .when('type', (type: IndicatorType, schema: yup.StringSchema) => {
        switch (type) {
          case IndicatorType.FAILURE:
            return schema.default(IndicatorColor.RED);
          case IndicatorType.WARNING:
            return schema.default(IndicatorColor.YELLOW);
          case IndicatorType.SUCCESS:
            return schema.default(IndicatorColor.GREEN);
        }
      })
      .default(IndicatorColor.RED),
  })
  .label('Indicator');

export const ButtonOptionsSchema: yup.SchemaOf<ButtonOptions> = yup
  .object({
    type: yup.mixed<ButtonOptions['type']>().default('button'),
    title: yup.string().required(),
    cta: yup.boolean().default(false),
    iconUrl: yup.string().default(''),
    index: yup.number(),
    onClick: yup.mixed().nullable(),
    submit: yup.boolean().default(false),
  })
  .label('ButtonOptions');

type DateToNumber<
  T extends NotificationOptions | BaseNotificationOptions<'markdown'>
> = DistributiveOmit<T, 'date' | 'expires'> & {
  date: number;
  expires: number | null;
};

export const ButtonsSchema = yup
  .array()
  .of(ButtonOptionsSchema)
  .transform((value: ButtonOptions[] = []) => {
    // Set button index
    return value.map((btn, index) => ({ ...btn, type: 'button', index: index }));
  })
  .test('Max 8 buttons', 'Max 8 buttons allowed', (value = []) => {
    return value.length <= 8;
  })
  .test('Max 4 CTA buttons', 'Max 4 CTA buttons allowed', (value = []) => {
    return value?.filter((x) => x.cta).length <= 4;
  })
  .test('Max 4 non-CTA buttons', 'Max 4 non-CTA buttons allowed', (value = []) => {
    return value?.filter((x) => !x.cta).length <= 4;
  })
  .default([]);

export const BaseNotificationSchema: yup.SchemaOf<
  DateToNumber<BaseNotificationOptions<'markdown'>>
> = yup
  .object({
    id: yup.string().default(() => generateId()),
    template: yup
      .mixed<keyof typeof TemplateNames>()
      .oneOf(Object.values(TemplateNames))
      .optional()
      .default('markdown'),
    title: yup.string().defined(),
    category: yup.string().defined(),
    indicator: IndicatorSchema.nullable().optional().default(null),
    icon: yup.string().default(''),
    priority: yup.number().min(1).max(4).default(1),
    customData: yup.object().optional().default({}),
    sticky: yup.mixed<'sticky' | 'transient'>().default('transient'),
    date: yup.number().default(() => Date.now()),
    expires: yup.number().nullable().default(null),
    stream: StreamSchema.nullable().optional().default(null),
    onSelect: yup.object().nullable().default(null),
    onClose: yup.object().nullable().default(null),
    onExpire: yup.object().nullable().default(null),
    buttons: ButtonsSchema.clone(),
  })
  .required();

const MarkdownTemplateSchema: yup.SchemaOf<
  DateToNumber<TemplateMarkdown>
> = BaseNotificationSchema.clone()
  .shape({
    template: yup.mixed<'markdown'>().required(),
    body: yup.string().defined(),
    form: formSchema.nullable().optional().default(null),
  })
  .label('Notification (Markdown)');

const ListTemplateSchema: yup.SchemaOf<DateToNumber<TemplateList>> = BaseNotificationSchema.clone()
  .omit(['template'])
  .shape({
    template: yup.mixed<'list'>().required(),
    list: yup
      .mixed<TemplateList['list']>()
      .required()
      .test({
        name: 'List object',
        message: 'Invalid list object',
        test: (value) => {
          if (typeof value !== 'object') {
            throw new Error('Not an object');
          }
          return Object.entries(value ?? {}).every(
            ([key, val]) => typeof key === 'string' && typeof val === 'string'
          );
        },
      }),
  })
  .label('Notification (List)');

const CustomTemplateSchema: yup.SchemaOf<
  DateToNumber<TemplateCustom>
> = BaseNotificationSchema.clone()
  .omit(['template'])
  .shape({
    template: yup.mixed<'custom'>().required(),
    templateOptions: yup
      .object()
      .shape({
        body: yup
          .object()
          .shape({
            fallbackText: yup.string().optional(),
            compositions: yup
              .array()
              .required()
              .min(1)
              .of(
                yup.object().shape({
                  minTemplateAPIVersion: yup
                    .string()
                    .required()
                    .test({
                      name: 'Template API Version',
                      message: 'Minimum Template API version must be a positive integer',
                      test: (value) => /^[0-9]+$/.test(value || '') && value !== '0',
                    }),
                  layout: yup.mixed<TemplateFragment>().required(),
                })
              ),
          })
          .required(),
        indicator: yup
          .object({
            align: yup.mixed<SectionAlignment>().oneOf(['center', 'left', 'right']).optional(),
            color: yup
              .mixed<IndicatorColor>()
              .oneOf(Object.values(IndicatorColor) as IndicatorColor[])
              .optional(),
          })
          .optional(),
        buttons: yup
          .object({
            align: yup.mixed<SectionAlignment>().oneOf(['center', 'left', 'right']).optional(),
          })
          .optional(),
      })
      .required(),
    templateData: yup.object().required(),
  })
  .label('Notification (Custom)');

export const CustomTemplateDataSchema = yup
  .mixed<CustomTemplateData>()
  .test({
    name: 'Custom Template Data',
    message: 'Custom Template Data Validation Error',
    test: function (value) {
      const fragments: FlatLayout = this.options?.context?.fragments;
      const data = value ?? {};
      const supportedFragments = Object.values(TemplateFragmentNames).filter(
        (name) => name != 'container'
      );
      const checkStyle = (fragment: TemplateFragment) => {
        if (fragment.style) {
          if (fragment.style.constructor !== Object) {
            throw new Error('"style" property must be an object type.');
          }
          for (const key in fragment.style) {
            const value = fragment.style[key];
            if (typeof value !== 'string' && typeof value !== 'number') {
              throw new Error(`"style" object's values must be a string or a number type.`);
            }
            /**
             * Disallow css properties that give the fragment the ability to render out of notification card.
             */
            if (
              typeof value === 'string' &&
              key.trim() === 'position' &&
              value.trim() === 'fixed'
            ) {
              throw new Error(`"${key}: ${value}" style is not allowed in fragments.`);
            }
          }
        }
      };
      fragments.containerFragments.forEach((fragment) => {
        if (fragment.children && !Array.isArray(fragment.children)) {
          throw new Error(`container fragment's "children" property must be an array type.`);
        }
        checkStyle(fragment);
      });
      fragments.presentationFragments.forEach((fragment) => {
        if (!supportedFragments.includes(fragment.type)) {
          throw new Error(
            `Unsupported fragment type (${
              fragment.type
            }) in composition. (Template API Version: ${getTemplateAPIVersion()})`
          );
        }
        if (!fragment.dataKey || typeof fragment.dataKey !== 'string') {
          throw new Error(
            '"dataKey" property must be a string type and is required field for all presentation fragment types.'
          );
        }
        if (fragment.optional && typeof fragment.optional !== 'boolean') {
          throw new Error('"optional" property must be a boolean type.');
        }
        const fragmentData = data[fragment.dataKey];
        if (!fragment.optional && !fragmentData) {
          throw new Error(
            `templateData["${fragment.dataKey}"] is not optional and must exist in template data.`
          );
        }
        if (fragmentData) {
          if (
            (fragment.type === 'text' || fragment.type === 'image') &&
            typeof fragmentData !== 'string'
          ) {
            throw new Error(
              `templateData["${fragment.dataKey}"] is referred by a ${fragment.type} fragment and must be a string type.`
            );
          }
          if (
            fragment.type === 'list' &&
            (!Array.isArray(fragmentData) ||
              fragmentData.some((pair) => {
                return (
                  pair.length !== 2 || typeof pair[0] !== 'string' || typeof pair[1] !== 'string'
                );
              }))
          ) {
            throw new Error(
              `templateData["${fragment.dataKey}"] is referred by a list fragment and must be a ListPairs (Array of [string, string]) type.`
            );
          }
        }
        checkStyle(fragment);
      });
      return true;
    },
  })
  .label('Custom Template Data');

// This is horrible we need to force template
export type MapTemplateSchema = Omit<
  {
    [K in GetTemplateName<NotificationOptions['template']>]: yup.SchemaOf<
      Extract<DateToNumber<NotificationOptions>, { template: K }>
    >;
  },
  'markdown'
> & {
  markdown: yup.SchemaOf<DateToNumber<TemplateMarkdown>>;
};

export const TemplateSchemaMap: MapTemplateSchema = {
  [TemplateNames.markdown]: MarkdownTemplateSchema,
  [TemplateNames.list]: ListTemplateSchema,
  [TemplateNames.custom]: CustomTemplateSchema,
};

/**
 * Create a uuid for a notification
 *
 */
export function generateId(): string {
  return v4();
}
